import { useCallback, useEffect, useState } from 'react'
import {
  deleteSunoStylePromptHistoryItem,
  getSunoStylePromptHistory,
  type SunoStylePromptHistoryItem
} from '../../../api/suno'

type UseStylePromptHistoryOptions = {
  toast: (message: string) => void
}

export const useStylePromptHistory = ({ toast }: UseStylePromptHistoryOptions) => {
  const [styleHistory, setStyleHistory] = useState<SunoStylePromptHistoryItem[]>([])
  const [styleHistoryLoading, setStyleHistoryLoading] = useState(false)
  const [styleHistoryError, setStyleHistoryError] = useState<string | null>(null)
  const [styleHistoryDeletingId, setStyleHistoryDeletingId] = useState<string | null>(null)
  const [styleHistoryOpen, setStyleHistoryOpen] = useState(false)

  const loadSunoStylePromptHistory = useCallback(async (): Promise<void> => {
    setStyleHistoryLoading(true)
    setStyleHistoryError(null)

    try {
      setStyleHistory(await getSunoStylePromptHistory())
    } catch (requestError) {
      setStyleHistoryError(requestError instanceof Error ? requestError.message : 'Unable to load prompt history.')
    } finally {
      setStyleHistoryLoading(false)
    }
  }, [])

  const deleteStyleHistoryItem = async (item: SunoStylePromptHistoryItem): Promise<void> => {
    setStyleHistoryDeletingId(item.id)
    try {
      await deleteSunoStylePromptHistoryItem(item.id)
      setStyleHistory((items) => items.filter((historyItem) => historyItem.id !== item.id))
      toast('Deleted history item.')
    } catch (requestError) {
      toast(requestError instanceof Error ? requestError.message : 'Unable to delete history item.')
    } finally {
      setStyleHistoryDeletingId(null)
    }
  }

  useEffect(() => {
    void loadSunoStylePromptHistory()
  }, [loadSunoStylePromptHistory])

  return {
    styleHistory,
    styleHistoryDeletingId,
    styleHistoryError,
    styleHistoryLoading,
    styleHistoryOpen,
    deleteStyleHistoryItem,
    setStyleHistory,
    setStyleHistoryOpen
  }
}
